/**
 * Date/time formatting for components.
 *
 * The one door: feature code never touches `Intl`, never resolves a locale or a
 * timezone itself, and never passes raw `Intl` options — it names a preset and
 * this hook supplies language + display timezone.
 *
 * Rules: `documents/conventions/datetime.md` (monorepo root).
 * @module @services/datetime
 */

import { useMemo } from 'react';
import {
  datePresetOptions,
  formatDate as formatDateValue,
  formatDateOnly as formatDateOnlyValue,
  formatDateTime as formatDateTimeValue,
  formatRelativeFromNow as formatRelativeFromNowValue,
  formatTime as formatTimeValue,
  resolveTimeZone,
  timePresetOptions,
  type DatePreset,
  type DateTimeInput,
  type TimePreset,
} from '@infra/format';
import { useTranslation } from '@infra/i18n';
import { getCurrentUser } from '@services/storage';

export interface DateTimeFormatter {
  /** Active UI language, for the rare caller that needs it (e.g. a memo key). */
  language: string;
  /** Timezone instants are displayed in: profile → browser → UTC. */
  timeZone: string;
  /**
   * An instant (`*At`) or an analytics bucket (`*Start`), shown in `timeZone`.
   *
   * Buckets need no special case: the server cuts them at midnight in the very
   * zone this hook reports (callers send it as the `timeZone` query param), so
   * rendering the returned instant here reproduces the boundary it was cut on.
   */
  formatDate: (value: DateTimeInput, preset?: DatePreset) => string;
  formatTime: (value: DateTimeInput, preset?: TimePreset) => string;
  formatDateTime: (value: DateTimeInput, preset?: DatePreset) => string;
  /**
   * An instant shown in a zone that belongs to the data, not the viewer — a
   * schedule owned by an organization reads in that organization's zone.
   */
  formatTimeInZone: (value: DateTimeInput, zone: string, preset?: TimePreset) => string;
  formatDateTimeInZone: (value: DateTimeInput, zone: string, preset?: DatePreset) => string;
  /** A calendar date (`*Date`, `YYYY-MM-DD`) — no timezone applies. */
  formatDateOnly: (value: string | null | undefined, preset?: DatePreset) => string;
  /** "5 minutes ago", unit chosen by the shared thresholds. */
  formatRelativeFromNow: (value: DateTimeInput) => string;
}

export function useDateTimeFormatter(): DateTimeFormatter {
  const { language } = useTranslation();
  // Read per render, not cached at module scope: changing the profile timezone
  // must take effect without a reload. If this ever shows up in a profile, lift
  // it to an app-level provider rather than caching here.
  const timeZone = resolveTimeZone(getCurrentUser()?.timezone);

  return useMemo(
    () => ({
      language,
      timeZone,
      formatDate: (value: DateTimeInput, preset: DatePreset = 'full') =>
        formatDateValue(value, language, { ...datePresetOptions(language, preset), timeZone }),
      formatTime: (value: DateTimeInput, preset: TimePreset = 'default') =>
        formatTimeValue(value, language, { ...timePresetOptions(preset), timeZone }),
      formatDateTime: (value: DateTimeInput, preset: DatePreset = 'full') =>
        formatDateTimeValue(value, language, {
          ...datePresetOptions(language, preset),
          ...timePresetOptions(),
          timeZone,
        }),
      formatTimeInZone: (value: DateTimeInput, zone: string, preset: TimePreset = 'default') =>
        formatTimeValue(value, language, { ...timePresetOptions(preset), timeZone: zone }),
      formatDateTimeInZone: (value: DateTimeInput, zone: string, preset: DatePreset = 'full') =>
        formatDateTimeValue(value, language, {
          ...datePresetOptions(language, preset),
          ...timePresetOptions(),
          timeZone: zone,
        }),
      formatDateOnly: (value: string | null | undefined, preset: DatePreset = 'full') =>
        formatDateOnlyValue(value, language, datePresetOptions(language, preset)),
      formatRelativeFromNow: (value: DateTimeInput) => formatRelativeFromNowValue(value, language),
    }),
    [language, timeZone]
  );
}
